import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/supabase';
import { FaHeart, FaComment, FaGift, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import './ViewPost.css';

// Helper function to resolve avatar URL
const resolveAvatarUrl = (filename, userFolderId) => {
  if (!filename) {
    // Default avatar SVG
    return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K";
  }
  
  // If already a full URL, return it
  if (typeof filename === 'string' && /^(http|https):\/\//i.test(filename)) {
    return filename;
  }
  // If data URL, return it
  if (typeof filename === 'string' && filename.startsWith('data:')) return filename;

  // Construct the proper path: avatars/<userId>/<filename>
  const path = `${userFolderId}/${filename}`;
  
  try {
    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    if (data?.publicUrl) {
      return data.publicUrl;
    }
  } catch (e) {
    console.log('[ViewPost] Error resolving avatar URL:', e);
  }

  return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K";
};

// Base URL for post media storage
const POST_MEDIA_BASE_URL = 'https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/post-media';

// Helper function to resolve media URL
const resolveMediaUrl = (filename, userId) => {
  if (!filename) return null;
  if (typeof filename === 'string' && /^(http|https):\/\//i.test(filename)) return filename;
  if (typeof filename === 'string' && filename.startsWith('data:')) return filename;
  
  try {
    return `${POST_MEDIA_BASE_URL}/${userId}/${filename}`;
  } catch (_) {
    return null;
  }
};

// Helper function to resolve audio URL
const resolveAudioUrl = (audioFilename, userId) => {
  if (!audioFilename) return null;
  if (typeof audioFilename === 'string' && /^(http|https):\/\//i.test(audioFilename)) return audioFilename;
  if (typeof audioFilename === 'string' && audioFilename.startsWith('data:')) return audioFilename;
  
  return `${POST_MEDIA_BASE_URL}/${userId}/${audioFilename}`;
};

export default function ViewPost() {
  const { username, postId, shareId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [fullscreenMediaIndex, setFullscreenMediaIndex] = useState(0);
  
  // Get translations - hook must be called unconditionally
  const { translations: tRaw } = useLanguage();
  const t = { downloadModal: tRaw?.downloadModal || {} };

  // Validate route - check if this looks like a ViewPost route
  // Check if the pathname starts with /@ or if postId is a UUID
  // Also verify it's actually a post, not a profile
  const [isValidPost, setIsValidPost] = useState(null);
  const isValidViewPostRoute = React.useMemo(() => {
    if (!username || !postId) return false;
    // Check if pathname starts with /@ (the actual URL)
    const pathStartsWithAt = location.pathname.startsWith('/@');
    // Check if postId looks like a UUID (has dashes and is 36 chars)
    const looksLikeUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(postId);
    // Also check if username starts with @ (it should if URL has @)
    const usernameHasAt = username.startsWith('@');
    return pathStartsWithAt || (looksLikeUUID && usernameHasAt);
  }, [username, postId, location.pathname]);

  // Quick check to verify this is actually a post, not a profile
  useEffect(() => {
    if (!postId || !isValidViewPostRoute) return;
    
    const checkIfPost = async () => {
      try {
        const { data, error } = await supabase
          .from('posts')
          .select('id')
          .eq('id', postId)
          .limit(1)
          .single();
        
        setIsValidPost(!error && data !== null);
      } catch (err) {
        setIsValidPost(false);
      }
    };
    
    checkIfPost();
  }, [postId, isValidViewPostRoute]);

  // Debug logging
  useEffect(() => {
    console.log('[ViewPost] Component mounted with params:', { 
      username, 
      postId, 
      shareId, 
      isValidViewPostRoute,
      pathname: location.pathname 
    });
    
    // If this doesn't look like a ViewPost route, it might be a different route
    // Don't redirect immediately - let it try to load and show error if needed
    if (!isValidViewPostRoute && !postId) {
      console.warn('[ViewPost] Route does not match ViewPost pattern. Missing postId.');
    }
  }, [username, postId, shareId, isValidViewPostRoute, location.pathname]);

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('[ViewPost] Starting to load post...', { postId, username, shareId });

        if (!postId) {
          setError('Post ID is required');
          setLoading(false);
          return;
        }

        // First, verify the share exists (optional)
        if (shareId) {
          try {
            const { data: shareData, error: shareError } = await supabase
              .from('shares')
              .select('*')
              .eq('share_id', shareId)
              .eq('post_id', postId)
              .single();

            if (shareError || !shareData) {
              console.log('[ViewPost] Share not found or invalid:', shareError);
              // Continue anyway - the share might not exist but post might
            }
          } catch (shareErr) {
            console.log('[ViewPost] Error checking share:', shareErr);
            // Continue anyway
          }
        }

        // Fetch post by ID
        console.log('[ViewPost] Fetching post with ID:', postId);
        const { data: postData, error: postError } = await supabase
          .from('posts')
          .select('*')
          .eq('id', postId)
          .eq('is_archived', false)
          .single();

        if (postError) {
          console.error('[ViewPost] Post fetch error:', postError);
          setError(`Post not found: ${postError.message}`);
          setLoading(false);
          return;
        }

        if (!postData) {
          console.error('[ViewPost] Post data is null');
          setError('Post not found');
          setLoading(false);
          return;
        }

        console.log('[ViewPost] Post found:', postData.id);

        // Fetch user profile by username (remove @ if present)
        // The username param will include the @ symbol if it was in the URL
        const cleanUsername = username ? username.replace(/^@+/, '') : null;
        console.log('[ViewPost] Fetching profile with username:', cleanUsername, '(original:', username, ')');
        
        let profileData = null;
        let profileError = null;

        if (cleanUsername) {
          const profileResult = await supabase
            .from('profiles')
            .select('*')
            .eq('username', cleanUsername)
            .single();
          
          profileData = profileResult.data;
          profileError = profileResult.error;
        }

        if (profileError || !profileData) {
          console.log('[ViewPost] Profile not found by username, trying user_id:', postData.user_id);
          // If username doesn't match, try to get profile from post user_id
          const { data: profileByUserId, error: profileByUserIdError } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', postData.user_id)
            .single();

          if (profileByUserIdError || !profileByUserId) {
            console.error('[ViewPost] Profile not found by user_id:', profileByUserIdError);
            setError('User profile not found');
            setLoading(false);
            return;
          }
          setProfile(profileByUserId);
          console.log('[ViewPost] Profile found by user_id:', profileByUserId.username);
        } else {
          // Verify the profile matches the post user_id
          if (profileData.user_id !== postData.user_id) {
            console.error('[ViewPost] Profile user_id mismatch');
            setError('Post does not belong to this user');
            setLoading(false);
            return;
          }
          setProfile(profileData);
          console.log('[ViewPost] Profile found by username:', profileData.username);
        }

        setPost(postData);
        console.log('[ViewPost] Post and profile loaded successfully');
      } catch (err) {
        console.error('[ViewPost] Error loading post:', err);
        setError(`Failed to load post: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (postId) {
      loadPost();
    } else {
      setError('Post ID is missing');
      setLoading(false);
    }
  }, [postId, username, shareId]);

  const handleInteraction = (type) => {
    setShowDownloadModal(true);
  };

  const handleNextMedia = (e) => {
    e.stopPropagation();
    if (post && post.media_names && post.media_names.length > 0) {
      setCurrentMediaIndex((prev) => (prev + 1) % post.media_names.length);
    }
  };

  const handlePrevMedia = (e) => {
    e.stopPropagation();
    if (post && post.media_names && post.media_names.length > 0) {
      setCurrentMediaIndex((prev) => (prev - 1 + post.media_names.length) % post.media_names.length);
    }
  };

  // Navigate to user profile - check authentication first
  const handleNavigateToProfile = async () => {
    if (!profile?.user_id) return;
    
    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session && session.user) {
        // User is authenticated
        // Check if the profile being clicked is the current user's profile
        if (session.user.id === profile.user_id) {
          // It's the authenticated user's own profile - go to /profile (own profile)
          navigate('/profile');
        } else {
          // It's someone else's profile - go to /profile/:uid
          navigate(`/profile/${profile.user_id}`);
        }
      } else {
        // User is not authenticated - navigate to public ViewProfile page
        // Format: /@username/userId/shareId/u
        const profileUsername = profile.username || profile.display_name || 'user';
        const viewShareId = shareId || 'view'; // Use existing shareId or 'view' as placeholder
        navigate(`/@${encodeURIComponent(profileUsername)}/${profile.user_id}/${viewShareId}/u`);
      }
    } catch (error) {
      console.error('[ViewPost] Error checking auth:', error);
      // On error, default to public ViewProfile page
      const profileUsername = profile.username || profile.display_name || 'user';
      const viewShareId = shareId || 'view';
      navigate(`/@${encodeURIComponent(profileUsername)}/${profile.user_id}/${viewShareId}/u`);
    }
  };

  // Fullscreen modal handlers
  const handleOpenFullscreen = () => {
    setFullscreenMediaIndex(currentMediaIndex);
    setShowFullscreenModal(true);
  };

  const handleCloseFullscreen = () => {
    setShowFullscreenModal(false);
  };

  const handleFullscreenNext = (e) => {
    e.stopPropagation();
    if (post && post.media_names && post.media_names.length > 0) {
      setFullscreenMediaIndex((prev) => (prev + 1) % post.media_names.length);
    }
  };

  const handleFullscreenPrev = (e) => {
    e.stopPropagation();
    if (post && post.media_names && post.media_names.length > 0) {
      setFullscreenMediaIndex((prev) => (prev - 1 + post.media_names.length) % post.media_names.length);
    }
  };

  const getDownloadUrl = () => {
    const appStoreUrl = 'https://apps.apple.com/ao/app/iwinto/id6482291656';
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.winto.app';

    if (typeof navigator === 'undefined') {
      return appStoreUrl;
    }

    const possibleOpera =
      typeof window !== 'undefined' && window.opera ? window.opera : '';
    const userAgent =
      navigator.userAgent || navigator.vendor || possibleOpera || '';
    const isAndroid = /android/i.test(userAgent);
    const isIOS =
      /iPad|iPhone|iPod/.test(userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isAndroid) {
      return playStoreUrl;
    }

    if (isIOS) {
      return appStoreUrl;
    }

    return appStoreUrl;
  };

  const handleDownloadClick = () => {
    const url = getDownloadUrl();

    if (typeof window === 'undefined') {
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Early check: if URL ends with /u, this is a profile route, not a post route
  // Return null immediately to let ViewProfile handle it
  if (location.pathname.endsWith('/u')) {
    console.log('[ViewPost] URL ends with /u, this is a profile route, not a post route');
    return null;
  }

  // Always show something to verify component is rendering
  if (loading) {
    return (
      <div className="view-post-container">
        <div className="view-post-loading">
          <p>Loading post...</p>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
            Debug: username={username}, postId={postId}, shareId={shareId}
          </p>
        </div>
      </div>
    );
  }

  // If this is not a valid post (might be a profile route), return null to let other routes handle it
  if (isValidPost === false && isValidViewPostRoute) {
    return null;
  }

  if (error || !post || !profile) {
    return (
      <div className="view-post-container">
        <div className="view-post-error">
          <p>{error || 'Post not found'}</p>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
            Debug: username={username}, postId={postId}, shareId={shareId}
          </p>
          {post && <p style={{ fontSize: '12px', color: '#999' }}>Post exists but profile missing</p>}
          {profile && <p style={{ fontSize: '12px', color: '#999' }}>Profile exists but post missing</p>}
        </div>
      </div>
    );
  }

  const avatarUrl = resolveAvatarUrl(profile.avatar_url, profile.user_id);
  const mediaFiles = post.media_names || [];
  const hasMultipleMedia = mediaFiles.length > 1;
  const currentMedia = mediaFiles[currentMediaIndex];
  const mediaUrl = currentMedia ? resolveMediaUrl(currentMedia, post.user_id) : null;
  const isVideo = currentMedia && (currentMedia.includes('.mp4') || currentMedia.includes('.mov') || currentMedia.includes('.webm'));
  const audioUrl = post.audio_filename ? resolveAudioUrl(post.audio_filename, post.user_id) : null;

  return (
    <div className="view-post-container">
      <div className="view-post-content">
        {/* User Header */}
        <div className="view-post-header">
          <img
            src={avatarUrl}
            alt={profile.username || profile.display_name}
            className="view-post-avatar view-post-avatar-clickable"
            onClick={handleNavigateToProfile}
          />
          <span 
            className="view-post-username view-post-username-clickable"
            onClick={handleNavigateToProfile}
          >
            {profile.display_name || profile.username || 'User'}
          </span>
        </div>

        {/* Media Section */}
        <div className="view-post-media-wrapper">
          {mediaUrl && (
            <div className="view-post-media-container">
              {hasMultipleMedia && (
                <button
                  className="view-post-carousel view-post-carousel-left"
                  onClick={handlePrevMedia}
                  aria-label="Previous media"
                >
                  ‹
                </button>
              )}
              
              {isVideo ? (
                <video
                  src={mediaUrl}
                  controls
                  className="view-post-media"
                  playsInline
                />
              ) : (
                <img
                  src={mediaUrl}
                  alt="Post media"
                  className="view-post-media view-post-media-clickable"
                  onClick={handleOpenFullscreen}
                />
              )}

              {hasMultipleMedia && (
                <button
                  className="view-post-carousel view-post-carousel-right"
                  onClick={handleNextMedia}
                  aria-label="Next media"
                >
                  ›
                </button>
              )}
            </div>
          )}
        </div>

        {/* Audio Section - Display if post has audio_filename */}
        {audioUrl && (
          <div className="view-post-audio-container">
            <audio
              controls
              src={audioUrl}
              className="view-post-audio"
            />
          </div>
        )}

        {/* Interaction Buttons */}
        <div className="view-post-actions">
          <button
            className="view-post-action-btn"
            onClick={() => handleInteraction('like')}
            aria-label="Like"
          >
            <FaHeart />
          </button>
          <button
            className="view-post-action-btn"
            onClick={() => handleInteraction('comment')}
            aria-label="Comment"
          >
            <FaComment />
          </button>
          <button
            className="view-post-action-btn"
            onClick={() => handleInteraction('donate')}
            aria-label="Donate"
          >
            <FaGift />
          </button>
        </div>
      </div>

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="view-post-modal-overlay" onClick={() => setShowDownloadModal(false)}>
          <div className="view-post-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="view-post-modal-title">
              {t?.downloadModal?.title || "Want to interact with this post?"}
            </h2>
            <p className="view-post-modal-subtitle">
              {t?.downloadModal?.subtitle || "Download the iWinto app to like, comment, and donate!"}
            </p>
            <div className="view-post-store-buttons">
              <button
                type="button"
                className="view-post-store-btn"
                onClick={handleDownloadClick}
              >
                {t?.downloadModal?.downloadApp || "Download the app"}
              </button>
            </div>
            <button
              className="view-post-modal-close"
              onClick={() => setShowDownloadModal(false)}
            >
              {t?.downloadModal?.close || "Close"}
            </button>
          </div>
        </div>
      )}

      {/* Fullscreen Media Modal */}
      {showFullscreenModal && post && mediaFiles.length > 0 && (
        <div className="view-post-fullscreen-overlay" onClick={handleCloseFullscreen}>
          <button 
            className="view-post-fullscreen-close-btn"
            onClick={handleCloseFullscreen}
            aria-label="Close"
          >
            <FaTimes />
          </button>
          
          <div className="view-post-fullscreen-content" onClick={(e) => e.stopPropagation()}>
            {mediaFiles.length > 1 && (
              <button
                className="view-post-fullscreen-arrow view-post-fullscreen-arrow-left"
                onClick={handleFullscreenPrev}
                aria-label="Previous"
              >
                <FaChevronLeft />
              </button>
            )}
            
            {(() => {
              const fullscreenMedia = mediaFiles[fullscreenMediaIndex];
              const fullscreenMediaUrl = fullscreenMedia ? resolveMediaUrl(fullscreenMedia, post.user_id) : null;
              const isFullscreenVideo = fullscreenMedia && (fullscreenMedia.includes('.mp4') || fullscreenMedia.includes('.mov') || fullscreenMedia.includes('.webm'));
              
              return isFullscreenVideo ? (
                <video
                  src={fullscreenMediaUrl}
                  controls
                  className="view-post-fullscreen-media"
                  playsInline
                  autoPlay
                />
              ) : (
                <img
                  src={fullscreenMediaUrl}
                  alt="Post media"
                  className="view-post-fullscreen-media"
                />
              );
            })()}
            
            {mediaFiles.length > 1 && (
              <button
                className="view-post-fullscreen-arrow view-post-fullscreen-arrow-right"
                onClick={handleFullscreenNext}
                aria-label="Next"
              >
                <FaChevronRight />
              </button>
            )}
          </div>
          
          {/* Image indicators */}
          {mediaFiles.length > 1 && (
            <div className="view-post-fullscreen-indicators">
              {mediaFiles.map((_, index) => (
                <span
                  key={index}
                  className={`view-post-fullscreen-indicator ${index === fullscreenMediaIndex ? 'active' : ''}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

